import axios from "axios";
import { baseURL } from "../utils/constants";

const publicApiClient = axios.create({
  baseURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const unwrap = <T>(res: { data: any }): T =>
  (res?.data?.result ?? res?.data) as T;

export interface HospitalBranding {
  hospital_id: string;
  hospital_name: string;
  short_name?: string;
  logo_url?: string;
  theme_color?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  contact_number?: string;
  contact_info?: { phone?: string; address?: string; timing?: string };
}

export interface PublicFeatureFlags {
  hospital_id: string;
  video_consultation?: boolean;
  payments?: boolean;
  raghvendra_payment_flow?: boolean;
  prn_collection?: boolean;
  language_change_buttons?: string[];
  no_walkin_button?: boolean;
  hide_cancel_token_slot?: boolean;
  hide_booking_after_xmin?: boolean;
  booking_window_weeks?: number;
  booking_expiry_duration?: number;
  multiple_of_slot_time?: number;
  visit_type_instruction_override?: boolean;
  no_dual_booking?: boolean;
  multiple_queues?: boolean;
  auto_slot_selection?: boolean;
  specialization_flow?: boolean;
  no_extend_session?: boolean;
  confirmation_template?: string;
  // Gates the OTP flow on the public widget. Defaults to true on the
  // backend if missing (fail-secure) — frontend should treat anything
  // other than an explicit `false` as OTP-required.
  public_booking_otp_required?: boolean;
  // Admin booking-screen features mirrored on the public page (15 Oct 2026).
  hims_patient_lookup?: boolean;
  appointment_program_enabled?: boolean;
  // Booking order + doctor picker: "<first>_<second>" of date and doctor /
  // specialization / both (see bookingFlow in PublicBooking). Default date_both.
  public_booking_flow?: PublicBookingFlow | null;
  // Replaced by public_booking_flow (5 Nov 2026); read only while a database
  // still has it. true = both_date.
  wa_web_v2_doc_first?: boolean;
  web_whatsapp_v2?: boolean;
  // Phone codes offered in the picker, digits only; null/missing = the default four.
  phone_country_codes?: string[] | null;
  // Hospital books in time blocks (30/60/120 min): the page offers blocks and
  // auto-picks the earliest free slot / token inside the chosen one.
  time_block_booking?: boolean;
  // Master kill-switch. When false, render the unavailable screen and
  // do not allow the widget to proceed.
  public_booking_enabled?: boolean;
  // Numeric caps — null = "unlimited, don't enforce client-side."
  public_max_bookings_per_day?: number | null;
  public_max_bookings_per_phone_per_day?: number | null;
  public_max_advance_days?: number | null;
  public_otp_daily_send_ceiling?: number | null;
  [key: string]: unknown;
}

// Backend returns the raw enum literal "Slot_Token" with an underscore
// (admin sanitizer normalizes to "SlotToken", but our public route is the
// no-sanitizer variant — so keep the underscore form on the wire).
export type QueueType = "Slot" | "Token" | "Slot_Token" | "Session";

export interface PublicDoctorAvailability {
  availability_id: string;
  queue_type?: QueueType;
  day_of_week?: number;
  start_time?: string;
  end_time?: string;
  session_start_time?: string;
  session_end_time?: string;
  wait_time?: number | { hours?: number; minutes?: number };
  max_patients_per_slot?: number;
  is_active?: boolean;
}

// A session's adjustment for one date, set from the staff booking screen
// ("Extend session") or the doctor's calendar (session off for the day).
export interface PublicAvailabilityAdjustment {
  availability_id: string;
  adjustment_date: string;
  type: "Extension" | "Unavailability";
  session_start_time: string | null;
  extended_end_time: string | null;
}

// The latest adjustment for this session on this date, or null when none.
export const getPublicAvailabilityAdjustment = async (
  availability_id: string,
  adjustment_date: string
): Promise<PublicAvailabilityAdjustment | null> => {
  const res = await publicApiClient.post(`/public/check-availability`, { availability_id, adjustment_date });
  const rows = unwrap<PublicAvailabilityAdjustment[] | null>(res);
  return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
};

export interface PublicDoctor {
  mapping_id: string;
  doctor_id: string;
  full_name: string;
  specialization?: string;
  qualification?: string;
  experience?: number | string;
  experience_years?: number | string;
  gender?: string;
  // From /public/hospital/:id/doctors?date= — false = no bookable session that day.
  available_on_date?: boolean;
  // web_whatsapp_v2 hospitals only: false = nothing left to book that day (no
  // session, sessions over, or every slot / token taken). Absent elsewhere.
  bookable?: boolean;
  // doctors.show_name_prefix: false = the hospital does not want "Dr." before this name.
  show_name_prefix?: boolean;
  consultation_fee?: number | string;
  estimated_visit_time?: number;
  mode?: string;
  profile_picture?: string;
  photo_url?: string;
  image_url?: string;
  pre_payment?: boolean;
  video_amount?: string | number;
  physical_amount?: string | number;
  availabilities?: PublicDoctorAvailability[];
  [key: string]: unknown;
}

export interface PublicPatient {
  patient_id: string;
  contact_number: string;
  full_name: string;
  gender?: string | null;
  date_of_birth?: string | null;
  lang?: string | null;
  email?: string | null;
}

// Returns a header object that's safe to spread into axios `headers`.
// Omits Authorization entirely when no token — preventing a literal
// "Bearer undefined" string from being sent.
const authHeaders = (
  token?: string | null
): Record<string, string> | undefined =>
  token ? { Authorization: `Bearer ${token}` } : undefined;

export interface OtpSendResponse {
  otp_sent: boolean;
  expires_in_seconds: number;
}

export const sendBookingOtp = async (params: {
  contact_number: string;
  hospital_id: string;
}): Promise<OtpSendResponse> => {
  const res = await publicApiClient.post(
    `/public/booking/send-otp`,
    params
  );
  return unwrap<OtpSendResponse>(res);
};

export interface OtpVerifyResponse {
  booking_token: string;
  expires_in_seconds: number;
}

export const verifyBookingOtp = async (params: {
  contact_number: string;
  hospital_id: string;
  otp: string;
}): Promise<OtpVerifyResponse> => {
  const res = await publicApiClient.post(
    `/public/booking/verify-otp`,
    params
  );
  return unwrap<OtpVerifyResponse>(res);
};

// bookingToken is required when the hospital has
// `public_booking_otp_required = true` (default). For hospitals that have
// opted out, the token is optional — caller passes null/undefined and the
// Authorization header is omitted.
export const searchPublicPatient = async (
  contact_number: string,
  hospital_id: string,
  bookingToken?: string | null
): Promise<PublicPatient | null> => {
  const res = await publicApiClient.get(`/public/patient/search`, {
    params: { contact_number, hospital_id },
    headers: authHeaders(bookingToken),
  });
  const data = unwrap<PublicPatient[] | PublicPatient | null>(res);
  if (!data) return null;
  if (Array.isArray(data)) return data[0] ?? null;
  return data;
};

export const getPublicHospital = async (
  hospital_id: string
): Promise<HospitalBranding> => {
  const res = await publicApiClient.get(`/public/hospital/${hospital_id}`);
  return unwrap<HospitalBranding>(res);
};

// A hospital group (parent + branches sharing one WhatsApp number): the bookable
// ones, by name. root_id = the group's root (the number owner); root_name = the
// group's name (the root's org_name when set, else its name).
export type PublicBranch = { hospital_id: string; name: string; address: string | null };
type PublicBranches = { root_id: string | null; root_name?: string | null; branches: PublicBranch[] };
export const getPublicHospitalBranches = async (hospital_id: string): Promise<PublicBranches> => {
  const res = await publicApiClient.get(`/public/hospital/${hospital_id}/branches`);
  return unwrap<PublicBranches>(res);
};

export const getPublicFeatureFlags = async (
  hospital_id: string
): Promise<PublicFeatureFlags | null> => {
  const res = await publicApiClient.get(`/public/featureflags/${hospital_id}`);
  const data = unwrap<PublicFeatureFlags | PublicFeatureFlags[]>(res);
  if (Array.isArray(data)) return data[0] ?? null;
  return data ?? null;
};

// Every active doctor with an available_on_date flag for the chosen day.
export const getPublicDoctors = async (
  hospital_id: string,
  date: string
): Promise<PublicDoctor[]> => {
  const res = await publicApiClient.get(`/public/hospital/${hospital_id}/doctors`, { params: { date } });
  const data = unwrap<PublicDoctor[]>(res);
  return Array.isArray(data) ? data : [];
};

// Dates on which one doctor has a bookable session (doctor-first flow).
export const getPublicDoctorAvailableDates = async (
  mapping_id: string,
  from: string,
  days: number
): Promise<string[]> => {
  const res = await publicApiClient.get(`/public/hospital/doctor/${mapping_id}/available-dates`, { params: { from, days } });
  const data = unwrap<string[]>(res);
  return Array.isArray(data) ? data : [];
};

export interface PublicTimeBlock {
  index: number;
  start: string; // "HH:MM:SS"
  end: string; // "HH:MM:SS"
  // Session queues only: each block holds an even share of the session cap.
  capacity?: number | null;
  remaining?: number | null;
  full?: boolean;
}
export interface PublicTimeBlocks {
  active: boolean;
  minutes: number | null;
  session_start: string | null;
  session_end: string | null;
  blocks: PublicTimeBlock[];
}
// The configured blocks of one session on one date ({active:false} = behave as before).
export const getPublicTimeBlocks = async (params: {
  availability_id: string;
  booked_date: string;
  /** Reschedule: the booking being moved, so it isn't counted against itself. */
  exclude_booking_id?: string;
}): Promise<PublicTimeBlocks> => {
  const res = await publicApiClient.post(`/public/availability/time-blocks`, params);
  const data = unwrap<PublicTimeBlocks>(res);
  return data && typeof data === "object" ? data : { active: false, minutes: null, session_start: null, session_end: null, blocks: [] };
};

export const getPublicOnlineDoctors = async (
  hospital_id: string,
  date: string
): Promise<PublicDoctor[]> => {
  const res = await publicApiClient.get(
    `/public/hospital/${hospital_id}/online-doctors`,
    { params: { date } }
  );
  const data = unwrap<PublicDoctor[] | { doctors?: PublicDoctor[] }>(res);
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as any).doctors)) return (data as any).doctors;
  return [];
};

export const getPublicDoctorAvailability = async (
  mapping_id: string
): Promise<PublicDoctorAvailability[]> => {
  const res = await publicApiClient.get(
    `/public/hospital/doctor/availability/${mapping_id}`
  );
  const data = unwrap<
    | PublicDoctorAvailability[]
    | { doctor_availability?: PublicDoctorAvailability[] }
  >(res);
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as any).doctor_availability))
    return (data as any).doctor_availability;
  return [];
};

export interface SlotTokenRequest {
  mapping_id: string;
  availability_id: string;
  booked_date: string; // YYYY-MM-DD
  start_time?: string;
  end_time?: string;
  number_of_people?: number;
  bookingType?: "booking" | "walk-in" | "video_consultation";
}

export interface PublicSlot {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
  available: boolean;
}

export interface PublicToken {
  token_number: number;
  available: boolean;
}

const parseHm = (t: string | undefined | null): number | null => {
  if (!t) return null;
  const [h, m] = String(t).split(":").map((s) => Number(s));
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
};

const fmtHm = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const waitTimeToMinutes = (
  w: number | { hours?: number; minutes?: number } | undefined | null
): number => {
  if (typeof w === "number") return w;
  if (w && typeof w === "object") {
    return (w.hours ? w.hours * 60 : 0) + (w.minutes || 0);
  }
  return 0;
};

interface BookedSlot {
  booked_slot_start?: string;
  booked_slot_end?: string;
}

const buildSlotsFromAvailability = (
  avail: PublicDoctorAvailability | undefined,
  booked: BookedSlot[]
): PublicSlot[] => {
  if (!avail) return [];
  const startM = parseHm(avail.start_time);
  const endM = parseHm(avail.end_time);
  const dur = waitTimeToMinutes(avail.wait_time);
  if (startM == null || endM == null || !dur || dur <= 0) return [];

  const cap = Number(avail.max_patients_per_slot) || 1;
  const counts = new Map<string, number>();
  for (const b of booked) {
    const k = (b?.booked_slot_start || "").slice(0, 5);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  }

  const out: PublicSlot[] = [];
  for (let m = startM; m + dur <= endM; m += dur) {
    const start = fmtHm(m);
    const end = fmtHm(m + dur);
    out.push({ start, end, available: (counts.get(start) || 0) < cap });
  }
  return out;
};

// Backend may return:
//  - flat array of numbers: [1, 2, 3, ...]
//  - object with tokens key: { tokens: [1, 2, 3, ...] }
//  - array of objects: [{ token_number, available }, ...]
// Backend filters booked tokens out — every returned token is treated as available.
const normalizeTokens = (raw: unknown): PublicToken[] => {
  const arr =
    Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object" && Array.isArray((raw as any).tokens)
      ? (raw as any).tokens
      : [];
  return (arr as unknown[])
    .map((entry): PublicToken | null => {
      if (typeof entry === "number") {
        return { token_number: entry, available: true };
      }
      if (entry && typeof entry === "object") {
        const e = entry as Record<string, unknown>;
        const num = Number(e.token_number ?? e.token ?? e.number);
        if (!Number.isFinite(num)) return null;
        return { token_number: num, available: e.available !== false };
      }
      return null;
    })
    .filter((t): t is PublicToken => t !== null);
};

export const getPublicSlots = async (
  payload: SlotTokenRequest
): Promise<PublicSlot[]> => {
  const res = await publicApiClient.post(`/public/slots`, payload);
  const data = unwrap<any>(res);

  // Already-shaped slot array.
  if (Array.isArray(data) && data.length && typeof data[0] === "object") {
    return data
      .map((s: any) => ({
        start: String(s.start || s.slot_start || "").slice(0, 5),
        end: String(s.end || s.slot_end || "").slice(0, 5),
        available: s.available !== false,
      }))
      .filter((s: PublicSlot) => s.start && s.end);
  }

  // { availability, booked_slots } — admin's raw shape, build client-side.
  if (data && typeof data === "object") {
    const availability: PublicDoctorAvailability | undefined = data.availability;
    const booked: BookedSlot[] = Array.isArray(data.booked_slots)
      ? data.booked_slots
      : [];
    if (availability) return buildSlotsFromAvailability(availability, booked);
    if (Array.isArray(data.slots)) {
      return data.slots
        .map((s: any) => ({
          start: String(s.start || s.slot_start || "").slice(0, 5),
          end: String(s.end || s.slot_end || "").slice(0, 5),
          available: s.available !== false,
        }))
        .filter((s: PublicSlot) => s.start && s.end);
    }
  }
  return [];
};

export const getPublicTokens = async (
  payload: SlotTokenRequest
): Promise<PublicToken[]> => {
  const res = await publicApiClient.post(`/public/tokens`, payload);
  return normalizeTokens(unwrap<any>(res));
};

export interface CreatePublicPatientPayload {
  hospital_id: string;
  full_name: string;
  contact_number: string;
  gender?: "Male" | "Female" | string;
  date_of_birth?: string | null;
  email?: string | null;
  address?: string | null;
  manager_contact_number?: string | null;
  lang?: string;
  marketing_consent?: boolean;
}

export const createPublicPatient = async (
  payload: CreatePublicPatientPayload,
  bookingToken?: string | null
): Promise<PublicPatient> => {
  const res = await publicApiClient.post(`/public/patient`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap<PublicPatient>(res);
};

// Per-queue payload contract (confirmed by backend):
//   - DO NOT send mapping_id (not a booking column)
//   - DO NOT send visit_type_id (not in schema)
//   - Slot       : booked_slot_start, booked_slot_end
//   - Token      : token_number
//   - Slot_Token : booked_slot_start, booked_slot_end, token_number
//   - Session    : session (int — count of session bookings)
//   - Optional fields must be OMITTED (undefined) when N/A — null fails the
//     validator's .optional().isInt() chain.
export interface CreatePublicBookingPayload {
  hospital_id: string;
  doctor_id: string;
  availability_id: string;
  patient_id: string;
  booked_date: string;
  booking_mode: "booking" | "walk-in";
  booking_type?: "physical" | "video_consultation";
  booking_lang?: string;
  is_confirmed?: boolean;
  symptoms?: string;
  comments?: string;
  queue_type: string;
  status: number;
  added_by?: string;
  booking_session_start_time?: string;
  booking_session_end_time?: string;
  booking_time?: string;
  prn_number?: string;
  // Queue-type-specific (omit when N/A):
  booked_slot_start?: string;
  booked_slot_end?: string;
  token_number?: number;
  session?: number;
  // visit_type_id mirrors AppointmentBooking's convention:
  //   1 = New
  //   2 = Follow Up
  // (See AppointmentBooking.tsx line ~1298 for the canonical mapping.)
  visit_type_id?: number;
}

export const createPublicBooking = async (
  payload: CreatePublicBookingPayload,
  bookingToken?: string | null
): Promise<unknown> => {
  const res = await publicApiClient.post(`/public/booking`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap(res);
};

// ── Admin booking-screen features on the public page (15 Oct 2026) ──────────
// Family members, the hospital's own patient record (MRN) and appointment
// programs. Same shapes the admin APIs use; all PHI calls take the booking
// token, which the backend only requires when the hospital has OTP on.

export interface HouseholdPerson {
  patient_id: string;
  full_name: string | null;
  gender?: string | null;
  date_of_birth?: string | null;
  contact_number?: string | null;
  manager_contact_number?: string | null;
  relationship_id?: string | null;
  relationship_type?: string | null;
  seen_at_hospital?: boolean;
}

export interface Household {
  self: HouseholdPerson | null;
  relations: HouseholdPerson[];
  verified: boolean;
}

export const getPublicHousehold = async (
  contact_number: string,
  hospital_id: string,
  bookingToken?: string | null
): Promise<Household> => {
  const res = await publicApiClient.get(`/public/patient/household`, {
    params: { contact_number, hospital_id },
    headers: authHeaders(bookingToken),
  });
  return unwrap<Household>(res);
};

// One-time exchange of the WhatsApp card's link token (?t=) for a booking
// session. 401 = already used / expired / tampered: fall back to the OTP path.
export const exchangeLinkSession = async (
  hospital_id: string,
  link_token: string
): Promise<{ booking_token: string; expires_in_seconds: number; phone: string }> => {
  const res = await publicApiClient.post(`/public/booking/link-session`, { hospital_id, link_token });
  return unwrap(res);
};

export const RELATION_TYPES = ["Father", "Mother", "Brother", "Sister", "Husband", "Wife", "Son", "Daughter", "Other"] as const;
export type RelationType = (typeof RELATION_TYPES)[number];

export interface AddHouseholdMemberPayload {
  hospital_id: string;
  contact_number: string; // the booker's phone
  full_name: string;
  gender?: "Male" | "Female";
  date_of_birth?: string | null;
  type: RelationType;
  booker_full_name?: string;
}

export const addPublicHouseholdMember = async (
  payload: AddHouseholdMemberPayload,
  bookingToken?: string | null
): Promise<{ booker_patient_id: string; patient: HouseholdPerson }> => {
  const res = await publicApiClient.post(`/public/patient/household`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap<{ booker_patient_id: string; patient: HouseholdPerson }>(res);
};

// What the admin screen does before booking for a registered patient: write
// the name / gender / date of birth typed on the form onto their record. Only
// the fields passed are sent; the backend refuses nulls so nothing is erased.
export const updatePublicPatient = async (
  patient_id: string,
  payload: { hospital_id: string; contact_number: string; full_name?: string; gender?: "Male" | "Female"; date_of_birth?: string },
  bookingToken?: string | null
): Promise<HouseholdPerson> => {
  const res = await publicApiClient.patch(`/public/patient/${patient_id}`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap<HouseholdPerson>(res);
};

/** One record as the hospital's own system holds it (same shape as the admin panel). */
export interface PublicHimsPatient {
  mrn: string;
  full_name: string;
  gender: string;
  date_of_birth: string | null;
  age: number | null;
  contact_number: string;
  guardian?: string;
  already_linked?: boolean;
  linked_patient?: { patient_id: string; full_name: string; contact_number: string } | null;
}
export interface PublicHimsMatched {
  patient_id: string;
  full_name: string;
  contact_number: string;
  date_of_birth?: string | null;
  gender?: string;
}
export type PublicHimsStatus = "ok" | "not_found" | "unreachable" | "off";

export const publicHimsSearch = async (
  hospital_id: string,
  contact_number: string,
  bookingToken?: string | null
): Promise<{ hims_status: PublicHimsStatus; vendor_name: string | null; hims: PublicHimsPatient[]; doctrue: PublicHimsMatched | null }> => {
  const res = await publicApiClient.get(`/public/hims/patients/search`, {
    params: { hospital_id, contact_number },
    headers: authHeaders(bookingToken),
  });
  return unwrap(res);
};

export const publicHimsLookup = async (
  hospital_id: string,
  contact_number: string,
  mrn: string,
  bookingToken?: string | null
): Promise<{ found: boolean; vendor_name: string | null; patient?: PublicHimsPatient; linked?: PublicHimsMatched | null; matched?: PublicHimsMatched | null; message?: string }> => {
  const res = await publicApiClient.get(`/public/hims/patients/lookup`, {
    params: { hospital_id, contact_number, mrn },
    headers: authHeaders(bookingToken),
  });
  return unwrap(res);
};

export const linkPublicHimsPatient = async (
  payload: { hospital_id: string; contact_number: string; patient_id: string; mrn: string },
  bookingToken?: string | null
): Promise<unknown> => {
  const res = await publicApiClient.post(`/public/hims/patients/link`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap(res);
};

export interface PublicProgram {
  program_id: number;
  program_code: string;
  program_name: string;
}

export const getPublicPrograms = async (
  hospital_id: string
): Promise<{ enabled: boolean; programs: PublicProgram[] }> => {
  const res = await publicApiClient.get(`/public/programs/${hospital_id}`);
  return unwrap(res);
};

export const setPublicBookingPrograms = async (
  payload: { hospital_id: string; contact_number: string; booking_id: string; program_ids: number[] },
  bookingToken?: string | null
): Promise<unknown> => {
  const res = await publicApiClient.put(`/public/booking/programs`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap(res);
};

// ── One booking's own pages (queue status / manage), opened from the
// web_whatsapp_v2 confirmation card with a booking-scoped token ───────────────
export interface PublicBookingDetail {
  booking_id: string;
  hospital_id: string;
  status: number;
  booking_mode?: string;
  booked_date: string;
  booking_session_start_time?: string | null;
  booking_session_end_time?: string | null;
  booked_slot_start?: string | null;
  booked_slot_end?: string | null;
  token_number?: number | null;
  time_block_start?: string | null;
  time_block_end?: string | null;
  visit_type_id?: number | null;
  patient_name?: string | null;
  hospital_name: string;
  hospital_address?: string | null;
  hospital_contact_number?: string | null;
  doctor_name?: string | null;
  doctor_specialization?: string | null;
  doctor_show_name_prefix?: boolean | null;
  // Set when the front desk moved the booking to another doctor.
  current_doctor_name?: string | null;
  current_doctor_specialization?: string | null;
  current_doctor_show_name_prefix?: boolean | null;
  transferred?: boolean;
  queue_type?: string | null;
  queue_ahead: number;
  token_in_consultation?: number | null;
  directions_url?: string | null;
  // The doctor's hospital mapping (reschedule reads their dates / sessions by it).
  mapping_id?: string | null;
  // What the manage page may offer; null when it couldn't be worked out.
  manage?: PublicManageRules | null;
  payment?: PublicPayment;
  // What happened to the booking, so an old link explains its current state.
  // Cancelled: when ("YYYY-MM-DD HH:mm", hospital time), by whom, and the
  // patient's own reason (a hospital's reason is never sent).
  cancellation?: { at: string | null; by: "patient" | "hospital"; reason: string | null } | null;
  // Rescheduled: the time before the latest move, when it moved, how many moves.
  reschedule?: {
    times: number;
    at: string | null;
    from: {
      booked_date: string;
      booking_session_start_time?: string | null;
      booking_session_end_time?: string | null;
      booked_slot_start?: string | null;
      booked_slot_end?: string | null;
      token_number?: number | null;
      doctor_name?: string | null; // only when the doctor changed
      doctor_show_name_prefix?: boolean | null;
    };
  } | null;
}

// Hospital settings + this booking's state, decided by the server.
export type ManageBlockedReason = "disabled" | "status" | "past" | "cutoff" | "payment_pending";
export type PublicBookingFlow =
  | "date_doctor"
  | "doctor_date"
  | "date_specialization"
  | "specialization_date"
  | "date_both"
  | "both_date";

export interface PublicManageRules {
  can_cancel: boolean;
  can_reschedule: boolean;
  cancel_blocked_reason: ManageBlockedReason | null;
  reschedule_blocked_reason: ManageBlockedReason | null;
  cutoff_minutes: number | null; // cancel closes this many minutes before the appointment
  deadline: string | null; // cancel deadline, "YYYY-MM-DD HH:mm", hospital time
  reschedule_cutoff_minutes?: number | null;
  reschedule_deadline?: string | null; // "YYYY-MM-DD HH:mm", hospital time
  // Latest date the booking may move to ("YYYY-MM-DD"); null = no limit.
  reschedule_max_days?: number | null;
  reschedule_last_date?: string | null;
  paid: boolean;
  max_advance_days: number | null;
}

// The doctor's fee, when they take one before the visit.
export interface PublicPayment {
  state: "not_required" | "pending" | "paid" | "expired";
  amount?: number | null;
  currency?: string;
  link?: string | null; // only while pending
  expires_at?: string | null;
}

export const cancelPublicBooking = async (
  booking_id: string,
  hospital_id: string,
  bookingToken: string,
  reason?: string
): Promise<{ booking_id: string; status: number }> => {
  const res = await publicApiClient.post(
    `/public/booking/${booking_id}/cancel`,
    { hospital_id, ...(reason ? { reason } : {}) },
    { headers: { Authorization: `Bearer ${bookingToken}` } }
  );
  return unwrap(res);
};

export interface ReschedulePayload {
  hospital_id: string;
  availability_id: string;
  booked_date: string;
  booked_slot_start?: string;
  booked_slot_end?: string;
  token_number?: number;
  /** Session queue with time blocks: the block picked ("HH:MM:SS"). */
  time_block_start?: string;
}
export const reschedulePublicBooking = async (
  booking_id: string,
  payload: ReschedulePayload,
  bookingToken: string
): Promise<{ booking_id: string; booked_date: string; availability_id: string }> => {
  const res = await publicApiClient.post(`/public/booking/${booking_id}/reschedule`, payload, {
    headers: { Authorization: `Bearer ${bookingToken}` },
  });
  return unwrap(res);
};

// The booking's own pages keep their token per tab under this key (the
// confirmation screen and the manage page share it).
export const bookingPageTokenKey = (bookingId: string) => `pb_booking_token_${bookingId}`;

export const getPublicBookingDetail = async (
  booking_id: string,
  hospital_id: string,
  bookingToken: string
): Promise<PublicBookingDetail> => {
  const res = await publicApiClient.get(`/public/booking/${booking_id}`, {
    params: { hospital_id },
    headers: { Authorization: `Bearer ${bookingToken}` },
  });
  return unwrap<PublicBookingDetail>(res);
};

// book.doctrue.in/go/<booking>/<kind>/<sig> (the confirmation template's buttons):
// the API verifies the signature and answers with where to go.
export const resolvePublicGoLink = async (
  booking_id: string,
  kind: string,
  sig: string
): Promise<{ url: string }> => {
  const res = await publicApiClient.get(`/public/go/${booking_id}/${kind}/${sig}`, { params: { format: "json" } });
  return unwrap<{ url: string }>(res);
};

// Live queue position for ONE booking (same data and rules as the TV queue).
export type PublicQueueState = "not_today" | "cancelled" | "completed" | "timeout" | "not_checked_in" | "in_queue" | "in_consultation";
export interface PublicQueueRow {
  position: number;
  token_number: number | null;
  status: number;
  status_name: string;
  category: "Inclinic" | "Scan" | "History";
  ongoing: boolean;
  is_you: boolean;
  name: string;
  room_name: string | null;
  on_hold?: boolean;
  emergency?: boolean;
}
export interface PublicBookingQueue {
  booking_id: string;
  booked_date: string;
  clinic_today: string;
  status: number;
  status_name: string;
  token_number: number | null;
  queue_type?: string | null;
  state: PublicQueueState;
  doctor?: {
    name: string;
    specialization?: string | null;
    in_clinic?: boolean | null; // null = unknown (transferred booking)
    arrived_at?: string | null; // "HH:mm", hospital time
    paused?: boolean;
    pause_reason?: string | null;
  } | null;
  on_hold?: boolean;
  emergency?: boolean;
  ahead_booked?: number | null; // before check-in: patients whose turn comes earlier
  in_clinic_count?: number; // checked in and waiting now
  transferred?: boolean;
  transferred_from?: string | null;
  session?: { availability_id: string; start_time?: string; end_time?: string; queue_type?: string } | null;
  position?: number | null;
  ahead?: number | null;
  now_serving?: number[];
  waiting?: number;
  queue?: PublicQueueRow[];
}
export const getPublicBookingQueue = async (
  booking_id: string,
  hospital_id: string,
  bookingToken: string
): Promise<PublicBookingQueue> => {
  const res = await publicApiClient.get(`/public/booking/${booking_id}/queue`, {
    params: { hospital_id },
    headers: { Authorization: `Bearer ${bookingToken}` },
  });
  return unwrap<PublicBookingQueue>(res);
};
